import {
  BIND_PREFIX,
  FORM_KEY_PROP,
  META_PREFIX,
  OPTION_LABEL_FIELD,
  OPTION_VALUE_FIELD,
} from '@de/utils/binding-enhancer/constants';
import type { BindDeclaration } from '@de/utils/binding-enhancer/types';
import { HOST_ONLY_CONTEXT_KEYS } from '@de/utils/decisions/embeddedDecisions';
import { warnOnce } from '@de/utils/warnOnce';

/**
 * Subset of `_bind.*` / `_meta.*` companion props on a Puck component
 * needed to translate a `formKey`-bearing selection into a DE
 * `Option`. Returned by {@link findBoundComponent} for downstream
 * label resolution.
 */
export interface BoundComponentInfo {
  /** Literal `formKey` prop value the component matched on. */
  readonly formKey: string;
  /**
   * Data-source name read from `_bind.options.source` (or
   * `_bind.value.source` as a fallback). Used to look up resolved
   * data for display-label resolution.
   */
  readonly source?: string;
  /**
   * Path within the source payload that identifies the array of
   * option entries — read from `_bind.options.path` (or
   * `_bind.value.path` as a fallback). Without this,
   * {@link resolveDisplayLabel} would only see the raw response
   * envelope (e.g. `{ degrees: [...] }`) and would never reach the
   * inner array, so every selection would fall back to its raw id
   * for `Option.text` instead of the human-readable label.
   *
   * The runtime grammar uses dotted identifiers + `[*]` selectors
   * (e.g. `degrees`, `degrees[*].categories`, see
   * `interpolation/parse.ts`). `[*]` paths can produce nested
   * arrays — display-label resolution deep-flattens them before
   * matching, mirroring the `applyOptions` transform's behaviour
   * so the same entries that rendered as choices are the same
   * entries we look up by `value`.
   */
  readonly optionsPath?: string;
  /** Field name within each source entry that holds the display label. */
  readonly optionLabelField?: string;
  /** Field name within each source entry that holds the value/slug. */
  readonly optionValueField?: string;
  /** `_meta.leadFieldAssociated`, when authored. */
  readonly leadFieldAssociated?: string;
  /**
   * `_meta.customField`, when authored. Distinct opt-in channel from
   * `leadFieldAssociated` for allowlisting a single dropdown/control
   * inside a multi-`formKey` step for `entryPoint.forwardFields`
   * (Discover eject `ctx_*` params) without affecting Lead submission.
   */
  readonly customField?: string;
  /**
   * `_meta.isArray`, when authored `true` on a DSL Select. Component-
   * level (mirrors `_meta.customField`): when set, the committed
   * {@link Option} carries `isArray: true` so `buildFlowContext`
   * expands a CSV `value` into a flat `string[]` on step-id /
   * `customField` context keys.
   */
  readonly isArray?: boolean;
  /** The authored `required` prop, when `true`. */
  readonly required?: boolean;
}

type ComponentNode = {
  readonly type?: unknown;
  readonly props?: Readonly<Record<string, unknown>>;
};

/** Puck type of the runtime primitive that scopes `formKey` fields to a named form. */
export const FORM_COMPONENT_TYPE = 'platform.form';

/** The `formKey` fields of one `platform.form` `name`; twin copies share one entry. */
export interface BoundFormFields {
  readonly name: string;
  /** `formKey` → bound info in document order; the first occurrence wins. */
  readonly fields: Readonly<Record<string, BoundComponentInfo>>;
  /** `leadFieldAssociated` → bound info; the first `formKey` per lead field wins. */
  readonly leadFields: Readonly<Record<string, BoundComponentInfo>>;
}

/** All maps here are prototype-free: read keys with `in` or `Object.prototype.hasOwnProperty.call`. */
export interface BoundFieldIndex {
  /** Form `name` → its fields, in document order. */
  readonly forms: Readonly<Record<string, BoundFormFields>>;
  /** `formKey` → name of its enclosing form. Keys outside every form are absent. */
  readonly formByKey: Readonly<Record<string, string>>;
}

/**
 * Walk a Puck `Data.content`-shaped tree and return the first
 * component whose `formKey` prop equals the supplied key.
 *
 * Slot props on Puck components hold arrays of nested `{ type, props }`
 * nodes; the walker recurses into any prop whose value is such an
 * array (or a single nested node). Non-matching trees return
 * `undefined` — callers must tolerate the miss without throwing.
 *
 * When the same `formKey` appears on more than one component the
 * function emits a single `console.warn` and returns the first match.
 * Repeats in distinct same-`name` `platform.form` nodes are twin copies and do not warn.
 * Authoring should treat this as a publish-time validation concern
 * (DB-3.A); the runtime falls back to the first occurrence so the
 * step still behaves predictably.
 */
export function findBoundComponent(
  content: unknown,
  formKey: string,
): BoundComponentInfo | undefined {
  if (!formKey || typeof formKey !== 'string') return undefined;
  const matches: BoundComponentInfo[] = [];
  const isDuplicate = createDuplicateTracker();
  let duplicated = false;
  walk(content, (key, props, scope) => {
    if (key !== formKey) return;
    if (isDuplicate(key, scope)) duplicated = true;
    matches.push(extractInfo(formKey, props));
  });
  if (matches.length === 0) return undefined;
  if (duplicated) {
    // eslint-disable-next-line no-console
    console.warn(
      `[ConfigurableStep] Multiple components share formKey "${formKey}"; using the first match.`,
    );
  }
  return matches[0];
}

/**
 * Walk a Puck `Data.content`-shaped tree once and return a
 * `formKey → BoundComponentInfo` index. The first occurrence of each
 * formKey wins (matching {@link findBoundComponent}'s "first match"
 * semantics); duplicates are reported via a single `console.warn`,
 * except twin copies in distinct same-`name` `platform.form` nodes.
 *
 * Used by `ConfigurableStep` to amortize the bound-component lookup
 * across `onFormStateChange` invocations (plan §3.10). Callers that
 * only need a single formKey should prefer {@link findBoundComponent}.
 */
export function buildBoundComponentIndex(
  content: unknown,
): Record<string, BoundComponentInfo> {
  const index = emptyRecord<BoundComponentInfo>();
  const isDuplicate = createDuplicateTracker();
  const warned = new Set<string>();
  walk(content, (key, props, scope) => {
    const duplicate = isDuplicate(key, scope);
    if (hasOwn(index, key)) {
      if (duplicate && !warned.has(key)) {
        warned.add(key);
        // eslint-disable-next-line no-console
        console.warn(
          `[ConfigurableStep] Multiple components share formKey "${key}"; using the first match.`,
        );
      }
      return;
    }
    index[key] = extractInfo(key, props);
  });
  return index;
}

/** Groups `formKey` fields of the static `data.content` tree by their innermost named `platform.form`. */
export function buildBoundFieldIndex(content: unknown): BoundFieldIndex {
  const forms = emptyRecord<MutableFormFields>();
  const formByKey = emptyRecord<string>();
  const outsideKeys = new Set<string>();
  const warned = new Set<string>();
  const warnInsideAndOutside = (formKey: string, formName: string): void => {
    if (warned.has(formKey)) return;
    warned.add(formKey);
    // eslint-disable-next-line no-console
    console.warn(
      `[ConfigurableStep] formKey "${formKey}" is inside platform.form "${formName}" and also outside a form; no copy of it is committed to flowState.`,
    );
  };
  const formFor = (name: string): MutableFormFields => {
    if (!hasOwn(forms, name)) {
      forms[name] = {
        name,
        fields: emptyRecord<BoundComponentInfo>(),
        leadFields: emptyRecord<BoundComponentInfo>(),
      };
    }
    return forms[name];
  };
  walk(
    content,
    (formKey, props, scope) => {
      if (scope === undefined) {
        outsideKeys.add(formKey);
        if (hasOwn(formByKey, formKey)) {
          warnInsideAndOutside(formKey, formByKey[formKey]);
        }
        return;
      }
      const formName = scope.name;
      if (outsideKeys.has(formKey)) warnInsideAndOutside(formKey, formName);
      if (!hasOwn(formByKey, formKey)) formByKey[formKey] = formName;
      const form = formFor(formName);
      if (hasOwn(form.fields, formKey)) return;
      const info = extractInfo(formKey, props);
      form.fields[formKey] = info;
      const leadField = info.leadFieldAssociated;
      if (leadField && !hasOwn(form.leadFields, leadField)) {
        form.leadFields[leadField] = info;
      }
    },
    formFor,
  );
  return { forms, formByKey };
}

interface MutableFormFields {
  readonly name: string;
  readonly fields: Record<string, BoundComponentInfo>;
  readonly leadFields: Record<string, BoundComponentInfo>;
}

/** Enclosing `platform.form`: its `name` plus the node, so twin copies can be told from repeats. */
interface FormScope {
  readonly name: string;
  readonly node: object;
}

function emptyRecord<T>(): Record<string, T> {
  return Object.create(null) as Record<string, T>;
}

function hasOwn(record: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

/** True for a repeat of a key unless every copy sits in a distinct node of one same-`name` form. */
function createDuplicateTracker(): (
  formKey: string,
  scope: FormScope | undefined,
) => boolean {
  const firstScope = new Map<string, FormScope | undefined>();
  const formNodes = new Map<string, Set<object>>();
  return (formKey, scope) => {
    if (!firstScope.has(formKey)) {
      firstScope.set(formKey, scope);
      if (scope) formNodes.set(formKey, new Set([scope.node]));
      return false;
    }
    const first = firstScope.get(formKey);
    const nodes = formNodes.get(formKey);
    if (!first || !scope || !nodes || first.name !== scope.name) return true;
    if (nodes.has(scope.node)) return true;
    nodes.add(scope.node);
    return false;
  };
}

type FormKeyVisitor = (
  formKey: string,
  props: Readonly<Record<string, unknown>>,
  scope: FormScope | undefined,
) => void;

function walk(
  node: unknown,
  visit: FormKeyVisitor,
  visitForm?: (name: string) => void,
  scope?: FormScope,
): void {
  if (Array.isArray(node)) {
    for (const item of node) walk(item, visit, visitForm, scope);
    return;
  }
  if (!isComponentNode(node)) return;

  const props = (node.props ?? {}) as Readonly<Record<string, unknown>>;
  const formKey = props[FORM_KEY_PROP];
  if (typeof formKey === 'string' && formKey.length > 0) {
    visit(formKey, props, scope);
  }

  const ownFormName = readFormName(node.type, props);
  if (ownFormName !== undefined) visitForm?.(ownFormName);
  const childScope =
    ownFormName === undefined || ownFormName === scope?.name
      ? scope
      : { name: ownFormName, node };

  for (const value of Object.values(props)) {
    if (Array.isArray(value) || isComponentNode(value)) {
      walk(value, visit, visitForm, childScope);
    }
  }
}

/**
 * Name of the `platform.form` node, trimmed, or `undefined` for any other node
 * and for a blank or non-string name. A non-blank name that is not
 * identifier-safe, contains `__` or is reserved is refused with one warning, so
 * the field index and the scope provider always agree on which forms exist.
 */
export function readFormName(
  type: unknown,
  props: Readonly<Record<string, unknown>>,
): string | undefined {
  if (type !== FORM_COMPONENT_TYPE) return undefined;
  const raw = props.name;
  if (typeof raw !== 'string') return undefined;
  const name = raw.trim();
  if (name.length === 0) return undefined;
  if (!isValidFormName(name)) {
    warnOnce(
      FORM_NAME_WARN_NAMESPACE,
      `[platform.form] Refusing name "${name}": use letters, numbers, hyphens and underscores, no "__", and not ${RESERVED_FORM_NAME_LIST}; the fields inside are not scoped to a form`,
    );
    return undefined;
  }
  return name;
}

/**
 * Names that would make a form's `leadSubmit__<name>__<suffix>` key equal an
 * unscoped `leadSubmit__<suffix>` or field-error key.
 */
export const RESERVED_FORM_NAMES: ReadonlySet<string> = new Set([
  'status',
  'busy',
  'error',
]);

const RESERVED_FORM_NAME_LIST = Array.from(RESERVED_FORM_NAMES)
  .map((reserved) => `"${reserved}"`)
  .join(', ');

const FORM_NAME_WARN_NAMESPACE = 'platform-form-name';
const FORM_NAME_PATTERN = /^[A-Za-z0-9_-]+$/;

function isValidFormName(name: string): boolean {
  return (
    FORM_NAME_PATTERN.test(name) &&
    !name.includes('__') &&
    !RESERVED_FORM_NAMES.has(name)
  );
}

function isComponentNode(value: unknown): value is ComponentNode {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    typeof (value as ComponentNode).type === 'string'
  );
}

/**
 * A `_meta` handle names the `flowContext` key a control's answer is written
 * to. A handle naming a host-only key is refused, so authored config cannot
 * write a rule result; the control itself still works.
 */
function readContextHandle(
  props: Readonly<Record<string, unknown>>,
  name: 'leadFieldAssociated' | 'customField',
): string | undefined {
  const handle = props[`${META_PREFIX}${name}`];
  if (typeof handle !== 'string') return undefined;
  if (HOST_ONLY_CONTEXT_KEYS.has(handle.trim())) {
    warnOnce(
      'host-only-context',
      `[ConfigurableStep] Refused \`${META_PREFIX}${name}: "${handle}"\` on control "${String(props.id ?? props[FORM_KEY_PROP] ?? '')}": context.${handle.trim()} is filled only by the page host.`,
    );
    return undefined;
  }
  return handle;
}

function extractInfo(
  formKey: string,
  props: Readonly<Record<string, unknown>>,
): BoundComponentInfo {
  const labelField = props[`${BIND_PREFIX}${OPTION_LABEL_FIELD}`];
  const valueField = props[`${BIND_PREFIX}${OPTION_VALUE_FIELD}`];
  const leadField = readContextHandle(props, 'leadFieldAssociated');
  const customField = readContextHandle(props, 'customField');
  const isArray = props[`${META_PREFIX}isArray`];
  // We pull `source` and `path` from the SAME bind declaration so a
  // future component that binds `_bind.value` (no `_bind.options`)
  // gets a consistent pair: same fallback chain, same decl. Picking
  // them independently could land `source` from `_bind.options` and
  // `path` from `_bind.value`, producing a nonsense traversal.
  const declRef = pickDeclaration(props);
  return {
    formKey,
    source: declRef?.source,
    optionsPath: declRef?.path,
    optionLabelField: typeof labelField === 'string' ? labelField : undefined,
    optionValueField: typeof valueField === 'string' ? valueField : undefined,
    leadFieldAssociated: typeof leadField === 'string' ? leadField : undefined,
    customField: typeof customField === 'string' ? customField : undefined,
    // Strict `=== true`: omit for absent/false/non-boolean so
    // `buildFlowContext` only expands when the author opted in.
    isArray: isArray === true ? true : undefined,
    required: props.required === true ? true : undefined,
  };
}

interface BindDeclRef {
  readonly source: string;
  readonly path?: string;
}

function pickDeclaration(
  props: Readonly<Record<string, unknown>>,
): BindDeclRef | undefined {
  const optionsBind = props[`${BIND_PREFIX}options`];
  const fromOptions = declRefOf(optionsBind);
  if (fromOptions) return fromOptions;
  const valueBind = props[`${BIND_PREFIX}value`];
  return declRefOf(valueBind);
}

function declRefOf(value: unknown): BindDeclRef | undefined {
  if (
    typeof value !== 'object' ||
    value === null ||
    typeof (value as BindDeclaration).source !== 'string'
  ) {
    return undefined;
  }
  const decl = value as BindDeclaration;
  return {
    source: decl.source,
    // `path` is only meaningful when it's a non-empty string; the
    // grammar rejects empty paths on the bind side too (see
    // `isBindDeclaration`), but defensive checks here keep
    // `resolveDisplayLabel` from feeding `parse('')` to the
    // interpolation grammar.
    path:
      typeof decl.path === 'string' && decl.path.length > 0
        ? decl.path
        : undefined,
  };
}
